import { FC, FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import "./styles.scss";
import { PageShell } from "../PageShell";
import { useTranslation } from "../../../hooks/useTranslation";
import { publicAPI } from "../../../backend/api/api";

// Initialize Stripe - only if key is provided (mirrors pages/BillingPage/index.tsx).
const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

type Frequency = "one_time" | "monthly";
type Step = "form" | "checkout" | "confirming" | "success";

const TIER_AMOUNTS = [18, 36, 100, 180, 360];

// Stripe webhooks can legitimately lag by a few seconds, so after Embedded
// Checkout's onComplete fires we give the backend a few chances to confirm
// the payment with Stripe before falling back to the "confirming" state.
const STATUS_POLL_MAX_ATTEMPTS = 3;
const STATUS_POLL_DELAY_MS = 1500;

const formatAmount = (amount: number): string =>
  Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);

/** The card fee on top of a gift, grossed up the way the server does (2.9% + 30 cents), so the gift arrives whole. */
const coverFeeText = (dollars: number): string => {
  const cents = Math.round(dollars * 100);
  const fee = Math.ceil((cents + 30) / (1 - 0.029)) - cents;
  return (fee / 100).toFixed(2);
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const DonatePage: FC = () => {
  const { p } = useTranslation("landing_donate");
  const { p: pc } = useTranslation("contact_us");

  const [step, setStep] = useState<Step>("form");
  const [frequency, setFrequency] = useState<Frequency>("one_time");
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  // Owner 2026-10-29: an unticked "cover the card fee" box, only while the Super Admin toggle is on.
  const [feeBox, setFeeBox] = useState(false);
  const [coverFee, setCoverFee] = useState(false);
  useEffect(() => {
    publicAPI.getDonationOptions().then((r) => setFeeBox(!!r.data?.cover_fee)).catch(() => setFeeBox(false));
  }, []);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const amountDollars = selectedTier !== null ? selectedTier : parseFloat(customAmount || "0") || 0;

  const handleTierClick = (tier: number) => {
    setSelectedTier(tier);
    setCustomAmount("");
    if (amountError) setAmountError(null);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedTier(null);
    if (amountError) setAmountError(null);
  };

  const validate = (): boolean => {
    let valid = true;

    if (!(amountDollars > 0)) {
      setAmountError(p("amount_required_error"));
      valid = false;
    } else {
      setAmountError(null);
    }

    if (!donorName.trim()) {
      setNameError(pc("name_required"));
      valid = false;
    } else {
      setNameError(null);
    }

    if (!donorEmail.trim()) {
      setEmailError(pc("email_required"));
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(donorEmail)) {
      setEmailError(pc("invalid_email"));
      valid = false;
    } else {
      setEmailError(null);
    }

    return valid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSubmitError(null);
    try {
      const { data } = await publicAPI.createDonationCheckoutSession({
        amount: Math.round(amountDollars * 100),
        frequency,
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim(),
        message: message.trim() || undefined,
        ...(feeBox && coverFee ? { cover_fee: true } : {}),
      });
      setClientSecret(data.client_secret);
      setSessionId(data.session_id);
      setStep("checkout");
    } catch (err) {
      setSubmitError(p("error_submit"));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setClientSecret(null);
    setSessionId(null);
    setStep("form");
  };

  // Stripe Embedded Checkout's onComplete only means the customer finished
  // the checkout form — it is NOT proof the payment (or our webhook) actually
  // succeeded. Reconcile with the backend before showing the success screen,
  // polling briefly since the webhook can lag a few seconds behind.
  const handleCheckoutComplete = async () => {
    setStep("confirming");

    if (!sessionId) return;

    for (let attempt = 0; attempt < STATUS_POLL_MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) await wait(STATUS_POLL_DELAY_MS);

      try {
        const { data } = await publicAPI.getDonationCheckoutSessionStatus(sessionId);
        if (data.status === "succeeded") {
          setStep("success");
          return;
        }
      } catch {
        // Ignore and retry — a transient network error shouldn't block
        // confirmation, and the "confirming" fallback below covers it.
      }
    }

    // Still pending after all attempts — leave the user on the "confirming"
    // state rather than a hard failure; the webhook + donor email will
    // catch up shortly.
  };

  // Walkthrough public #9/#29: the button names what is charged (the gift plus the card fee when that box is ticked), and no "$0".
  const chargedDollars = feeBox && coverFee && amountDollars > 0 ? (Math.round(amountDollars * 100) + Math.round(parseFloat(coverFeeText(amountDollars)) * 100)) / 100 : amountDollars;
  const submitLabel = loading
    ? p("submitting")
    : !(amountDollars > 0) ? p("submit_no_amount")
    : p(frequency === "monthly" ? "submit_monthly" : "submit", { amount: chargedDollars === amountDollars ? formatAmount(amountDollars) : chargedDollars.toFixed(2) });

  return (
    <PageShell>
      <div className="landing-donate-page">
        <div className="landing-donate-page__inner">
          <h1 className="landing-donate-page__heading">{p("heading")}</h1>
          <p className="landing-donate-page__subtitle">{p("subtitle")}</p>

          <div className="landing-donate-page__card">
            {step === "success" ? (
              <div className="landing-donate-page__success">
                <div className="landing-donate-page__success-icon" aria-hidden="true">✓</div>
                <h2 className="landing-donate-page__success-heading">
                  {p(frequency === "monthly" ? "success_heading_monthly" : "success_heading_one_time")}
                </h2>
                <p className="landing-donate-page__success-text">{p("success_text")}</p>
              </div>
            ) : step === "confirming" ? (
              <div className="landing-donate-page__success">
                <h2 className="landing-donate-page__success-heading">{p("confirming_heading")}</h2>
                <p className="landing-donate-page__success-text">{p("confirming_text")}</p>
              </div>
            ) : step === "checkout" && clientSecret && stripePromise ? (
              <div className="landing-donate-page__checkout">
                <button type="button" className="landing-donate-page__back-link" onClick={handleBack}>
                  {p("back_to_form")}
                </button>
                <div className="landing-donate-page__checkout-wrapper">
                  <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{ clientSecret, onComplete: () => void handleCheckoutComplete() }}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </div>
              </div>
            ) : (
              <form className="landing-donate-page__form" onSubmit={handleSubmit} noValidate>
                {submitError && (
                  <p className="landing-donate-page__form-error" role="alert">{submitError}</p>
                )}

                <div className="landing-donate-page__frequency" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={frequency === "one_time"}
                    className={
                      "landing-donate-page__frequency-tab" +
                      (frequency === "one_time" ? " landing-donate-page__frequency-tab--active" : "")
                    }
                    onClick={() => setFrequency("one_time")}
                  >
                    {p("frequency_one_time")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={frequency === "monthly"}
                    className={
                      "landing-donate-page__frequency-tab" +
                      (frequency === "monthly" ? " landing-donate-page__frequency-tab--active" : "")
                    }
                    onClick={() => setFrequency("monthly")}
                  >
                    {p("frequency_monthly")}
                  </button>
                </div>

                <div className="landing-donate-page__field">
                  <label className="landing-donate-page__label">{p("amount_label")}</label>
                  <div className="landing-donate-page__tiers">
                    {TIER_AMOUNTS.map((tier) => (
                      <button
                        key={tier}
                        type="button"
                        className={
                          "landing-donate-page__tier" +
                          (selectedTier === tier ? " landing-donate-page__tier--active" : "")
                        }
                        onClick={() => handleTierClick(tier)}
                      >
                        {frequency === "monthly" ? `$${tier}/mo` : `$${tier}`}
                      </button>
                    ))}
                  </div>

                  <label className="landing-donate-page__label" htmlFor="donate-custom-amount">
                    {p("custom_amount_label")}
                  </label>
                  <input
                    id="donate-custom-amount"
                    type="number"
                    min={1}
                    step="0.01"
                    inputMode="decimal"
                    className={
                      "landing-donate-page__input" +
                      (amountError ? " landing-donate-page__input--error" : "")
                    }
                    placeholder={p("custom_amount_placeholder")}
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                  />
                  {amountError && <span className="landing-donate-page__field-error">{amountError}</span>}
                </div>

                {feeBox && amountDollars > 0 && (
                  <label className="landing-donate-page__check">
                    <input type="checkbox" checked={coverFee} onChange={(e) => setCoverFee(e.target.checked)} />
                    <span>{p("cover_fee", { fee: coverFeeText(amountDollars) })}</span>
                  </label>
                )}

                <p className="landing-donate-page__tax-note">{p("tax_note")}</p>

                <div className="landing-donate-page__field">
                  <label className="landing-donate-page__label" htmlFor="donate-name">
                    {p("donor_name_label")}
                  </label>
                  <input
                    id="donate-name"
                    className={
                      "landing-donate-page__input" +
                      (nameError ? " landing-donate-page__input--error" : "")
                    }
                    value={donorName}
                    onChange={(e) => {
                      setDonorName(e.target.value);
                      if (nameError) setNameError(null);
                    }}
                  />
                  {nameError && <span className="landing-donate-page__field-error">{nameError}</span>}
                </div>

                <div className="landing-donate-page__field">
                  <label className="landing-donate-page__label" htmlFor="donate-email">
                    {p("donor_email_label")}
                  </label>
                  <input
                    id="donate-email"
                    type="email"
                    className={
                      "landing-donate-page__input" +
                      (emailError ? " landing-donate-page__input--error" : "")
                    }
                    value={donorEmail}
                    onChange={(e) => {
                      setDonorEmail(e.target.value);
                      if (emailError) setEmailError(null);
                    }}
                  />
                  {emailError ? (
                    <span className="landing-donate-page__field-error">{emailError}</span>
                  ) : (
                    <span className="landing-donate-page__field-hint">{p("donor_email_hint")}</span>
                  )}
                </div>

                <div className="landing-donate-page__field">
                  <label className="landing-donate-page__label" htmlFor="donate-message">
                    {p("message_label")}
                    <span className="landing-donate-page__label-optional">{pc("optional")}</span>
                  </label>
                  <textarea
                    id="donate-message"
                    className="landing-donate-page__input landing-donate-page__input--textarea"
                    rows={4}
                    placeholder={p("message_placeholder")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>

                <button type="submit" className="landing-donate-page__submit" disabled={loading}>
                  {submitLabel}
                </button>
              </form>
            )}
          </div>

          {step === "form" && (
            <p className="landing-donate-page__alt-giving">
              {p("contact_prompt")}
              <Link to="/contact-us">{p("contact_link")}</Link>
            </p>
          )}
        </div>
      </div>
    </PageShell>
  );
};
